import React, { useEffect, useState } from "react";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import api from "../../services/api";
import { useLanguage } from "../../context/LanguageContext";
import { useSnackbar } from "../../context/SnackbarContext";

const roleOptions = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "guest", label: "Guest" },
];

export default function UserManagement() {
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const errorMessageFromResponse = (err) => {
    const code = err?.response?.data?.code;
    if (code === "cannot_deactivate_self")
      return t("cannotDeactivateSelf") || "You cannot deactivate your own account.";
    if (code === "cannot_deactivate_superuser")
      return t("cannotDeactivateSuperuser") || "You cannot deactivate a superuser.";
    if (code === "cannot_deactivate_primary_admin")
      return (
        t("cannotDeactivatePrimaryAdmin") || "You cannot deactivate the primary admin account."
      );
    return err?.response?.data?.detail || err?.message || "Request failed";
  };
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState("all");
  const [isAdmin, setIsAdmin] = useState(false);

  // Modals
  const [assignModal, setAssignModal] = useState({ open: false, user: null });
  const [manageCompaniesModal, setManageCompaniesModal] = useState({ open: false, user: null });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [passwordModal, setPasswordModal] = useState({ open: false, user: null });

  // Drafts
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [passwordDraft, setPasswordDraft] = useState({ p1: "", p2: "" });
  const [draft, setDraft] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "user",
    is_active: true,
    company_ids: [],
    password: "",
  });

  // Fetchers
  const fetchAdminMe = async () => {
    const { data } = await api.get("/api/admin/me/");
    setIsAdmin(Boolean(data.is_admin));
  };

  const fetchUsers = async () => {
    const params = {};
    if (selectedCompanyFilter !== "all") params.company_id = selectedCompanyFilter;
    const { data } = await api.get("/api/admin/users/", { params });
    setUsers(data.results || []);
  };

  const fetchCompanies = async () => {
    const { data } = await api.get("/api/admin/companies/");
    setCompanies(data.results || []);
  };

  useEffect(() => {
    fetchAdminMe();
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin, selectedCompanyFilter]);

  // Actions
  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/api/admin/users/${userId}/role/`, { role: newRole });
      showSnackbar(t("roleUpdated") || "Role updated", "success");
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to update role";
      showSnackbar(msg, "error");
      fetchUsers();
    }
  };

  const handleToggleActive = async (userId, newActive) => {
    try {
      await api.put(`/api/admin/users/${userId}/active/`, { is_active: newActive });
      showSnackbar(
        newActive
          ? t("userActivated") || "User activated"
          : t("userDeactivated") || "User deactivated",
        "success"
      );
      fetchUsers();
    } catch (err) {
      const msg = errorMessageFromResponse(err);
      showSnackbar(msg, "error");
      fetchUsers();
    }
  };

  const openAssignCompany = (user) => {
    setAssignModal({ open: true, user });
    setSelectedCompanyId("");
  };

  const openManageCompanies = (user) => {
    setManageCompaniesModal({ open: true, user });
    setSelectedCompanyId("");
  };

  const addUserToCompany = async () => {
    if (!assignModal.user || !selectedCompanyId) return;
    try {
      await api.post(`/api/admin/users/${assignModal.user.id}/companies/add/`, {
        company_id: selectedCompanyId,
      });
      showSnackbar(t("assignedToCompany") || "Assigned to company", "success");
      setAssignModal({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to assign to company";
      showSnackbar(msg, "error");
    }
  };

  const addAllCompaniesToUser = async () => {
    if (!assignModal.user) return;
    let ok = 0;
    let fail = 0;
    for (const c of companies) {
      try {
        await api.post(`/api/admin/users/${assignModal.user.id}/companies/add/`, {
          company_id: c.id,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    if (fail === 0) {
      showSnackbar(t("assignedToCompany") || "Assigned to all companies", "success");
    } else {
      showSnackbar(`Priskirta: ${ok}, nepavyko: ${fail}`, "warning");
    }
    setAssignModal({ open: false, user: null });
    fetchUsers();
  };

  const removeUserFromCompany = async (userId, companyId) => {
    try {
      await api.post(`/api/admin/users/${userId}/companies/remove/`, { company_id: companyId });
      showSnackbar(t("removedFromCompany") || "Removed from company", "success");
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to remove from company";
      showSnackbar(msg, "error");
    }
  };

  const openCreateUser = () => {
    setDraft({
      email: "",
      first_name: "",
      last_name: "",
      role: "user",
      is_active: true,
      company_ids: [],
      password: "",
    });
    setCreateModalOpen(true);
  };

  const createUser = async () => {
    const payload = { ...draft };
    // Only include company_ids if not empty
    if (!payload.company_ids || payload.company_ids.length === 0) delete payload.company_ids;
    try {
      await api.post("/api/admin/users/create/", payload);
      showSnackbar(t("userCreated") || "User created", "success");
      setCreateModalOpen(false);
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to create user";
      showSnackbar(msg, "error");
    }
  };

  const openPassword = (user) => {
    setPasswordDraft({ p1: "", p2: "" });
    setPasswordModal({ open: true, user });
  };

  const setNewPassword = async () => {
    if (!passwordModal.user || !passwordDraft.p1) return;
    try {
      await api.post(`/api/admin/users/${passwordModal.user.id}/password/`, {
        new_password: passwordDraft.p1,
      });
      showSnackbar(t("passwordUpdated") || "Password updated", "success");
      setPasswordModal({ open: false, user: null });
      setPasswordDraft({ p1: "", p2: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to set password";
      showSnackbar(msg, "error");
    }
  };

  const updateUserProfile = async (userId, patch) => {
    try {
      await api.put(`/api/admin/users/${userId}/profile/`, patch);
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to update user";
      showSnackbar(msg, "error");
      fetchUsers();
    }
  };

  // Shared widths and cell styles to keep header and body perfectly aligned
  const colWidths = [240, 160, 160, 140, 180, 320, 280];
  const thStyleBase = {
    border: "1px solid #e5e7eb",
    padding: 8,
    fontWeight: 600,
    boxSizing: "border-box",
  };
  const tdStyleBase = {
    border: "1px solid #e5e7eb",
    padding: 8,
    verticalAlign: "top",
    boxSizing: "border-box",
  };
  const inputStyle = { width: "100%", boxSizing: "border-box", padding: "6px 8px" };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDTypography variant="h6">{t("noAccess") || "No access"}</MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <MDTypography variant="h6" color="white">
                {t("userManagement") || "User Management"}
              </MDTypography>
              <MDButton onClick={openCreateUser} color="white" variant="outlined">
                {t("addUser") || "Add user"}
              </MDButton>
            </Box>
          </MDBox>
          <MDBox p={3}>
            <Box display="flex" gap={2} alignItems="center" mb={2}>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="company-filter-label">
                  {t("filterByCompany") || "Filter by company"}
                </InputLabel>
                <Select
                  labelId="company-filter-label"
                  value={selectedCompanyFilter}
                  label={t("filterByCompany") || "Filter by company"}
                  onChange={(e) => setSelectedCompanyFilter(e.target.value)}
                >
                  <MenuItem value="all">{t("all") || "All"}</MenuItem>
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <MDBox sx={{ overflowX: "auto" }}>
              {/* Plain HTML table to avoid any header/body misalignment */}
              <table style={{ tableLayout: "fixed", width: "100%", borderCollapse: "collapse" }}>
                <colgroup>
                  {colWidths.map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <th style={{ ...thStyleBase, width: colWidths[0] }}>{t("email") || "Email"}</th>
                    <th style={{ ...thStyleBase, width: colWidths[1] }}>
                      {t("firstName") || "First name"}
                    </th>
                    <th style={{ ...thStyleBase, width: colWidths[2] }}>
                      {t("lastName") || "Last name"}
                    </th>
                    <th style={{ ...thStyleBase, width: colWidths[3] }}>{t("role") || "Role"}</th>
                    <th style={{ ...thStyleBase, width: colWidths[4] }}>
                      {t("active") || "Active"}
                    </th>
                    <th style={{ ...thStyleBase, width: colWidths[5] }}>
                      {t("company") || "Company"}
                    </th>
                    <th style={{ ...thStyleBase, width: colWidths[6] }}>
                      {t("actions") || "Actions"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const companiesList = Array.isArray(u.companies) ? u.companies : [];
                    const maxVisible = 3;
                    const visibleCompanies = companiesList.slice(0, maxVisible);
                    const hiddenCompanies = companiesList.slice(maxVisible);
                    const hiddenTooltip = hiddenCompanies.map((c) => c.name).join(", ");

                    return (
                      <tr key={u.id}>
                        <td style={{ ...tdStyleBase, width: colWidths[0] }}>
                          <div style={{ maxWidth: colWidths[0] }}>
                            <input
                              style={inputStyle}
                              defaultValue={u.email}
                              onBlur={(e) => {
                                const val = e.target.value;
                                if (val !== u.email) updateUserProfile(u.id, { email: val });
                              }}
                            />
                          </div>
                        </td>
                        <td style={{ ...tdStyleBase, width: colWidths[1] }}>
                          <div style={{ maxWidth: colWidths[1] }}>
                            <input
                              style={inputStyle}
                              defaultValue={u.first_name}
                              onBlur={(e) => {
                                const val = e.target.value;
                                if (val !== u.first_name)
                                  updateUserProfile(u.id, { first_name: val });
                              }}
                            />
                          </div>
                        </td>
                        <td style={{ ...tdStyleBase, width: colWidths[2] }}>
                          <div style={{ maxWidth: colWidths[2] }}>
                            <input
                              style={inputStyle}
                              defaultValue={u.last_name}
                              onBlur={(e) => {
                                const val = e.target.value;
                                if (val !== u.last_name)
                                  updateUserProfile(u.id, { last_name: val });
                              }}
                            />
                          </div>
                        </td>
                        <td style={{ ...tdStyleBase, width: colWidths[3] }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={u.role}
                              onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            >
                              {roleOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {t(opt.value) || opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </td>
                        <td style={{ ...tdStyleBase, width: colWidths[4] }}>
                          <ToggleButtonGroup
                            color="primary"
                            value={u.is_active ? "active" : "inactive"}
                            exclusive
                            onChange={(e, val) => {
                              if (!val) return;
                              handleToggleActive(u.id, val === "active");
                            }}
                            size="small"
                          >
                            <ToggleButton value="active">{t("active") || "Active"}</ToggleButton>
                            <ToggleButton value="inactive">
                              {t("inactive") || "Inactive"}
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </td>
                        <td style={{ ...tdStyleBase, width: colWidths[5] }}>
                          <Box
                            display="flex"
                            gap={0.5}
                            flexWrap="wrap"
                            sx={{ maxWidth: colWidths[5] }}
                          >
                            {visibleCompanies.map((c) => (
                              <Chip
                                key={c.id}
                                size="small"
                                variant="outlined"
                                label={c.name}
                                onDelete={() => removeUserFromCompany(u.id, c.id)}
                              />
                            ))}
                            {hiddenCompanies.length > 0 && (
                              <Tooltip title={t("showAll") || hiddenTooltip} placement="top" arrow>
                                <Chip
                                  size="small"
                                  label={`+${hiddenCompanies.length}`}
                                  onClick={() => openManageCompanies(u)}
                                  clickable
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </td>
                        <td style={{ ...tdStyleBase, width: colWidths[6] }}>
                          <Box display="flex" flexDirection="column" gap={1}>
                            <MDButton
                              fullWidth
                              variant="outlined"
                              color="info"
                              size="small"
                              sx={{ whiteSpace: "nowrap" }}
                              onClick={() => openAssignCompany(u)}
                            >
                              {t("assignToCompany") || "Assign to company"}
                            </MDButton>
                            <MDButton
                              fullWidth
                              variant="outlined"
                              color="warning"
                              size="small"
                              sx={{ whiteSpace: "nowrap" }}
                              onClick={() => openPassword(u)}
                            >
                              {t("setPassword") || "Set password"}
                            </MDButton>
                          </Box>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />

      {/* Assign Company Dialog */}
      <Dialog open={assignModal.open} onClose={() => setAssignModal({ open: false, user: null })}>
        <DialogTitle>{t("assignToCompany") || "Assign to company"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="assign-company-label">{t("company") || "Company"}</InputLabel>
            <Select
              labelId="assign-company-label"
              value={selectedCompanyId}
              label={t("company") || "Company"}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
            >
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <MDButton
            onClick={() => setAssignModal({ open: false, user: null })}
            color="secondary"
            variant="outlined"
          >
            {t("cancel") || "Cancel"}
          </MDButton>
          <MDButton onClick={addAllCompaniesToUser} color="warning" variant="contained">
            {t("selectAllCompanies") || "Pasirinkti visas įmones"}
          </MDButton>
          <MDButton onClick={addUserToCompany} color="info" variant="contained">
            {t("assign") || "Assign"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <DialogTitle>{t("addUser") || "Add user"}</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} flexDirection="column" sx={{ mt: 1, minWidth: 420 }}>
            <label>{t("email") || "Email"}</label>
            <input
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />

            <label>{t("firstName") || "First name"}</label>
            <input
              value={draft.first_name}
              onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
            />

            <label>{t("lastName") || "Last name"}</label>
            <input
              value={draft.last_name}
              onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
            />

            <label>{t("password") || "Password"}</label>
            <input
              type="password"
              value={draft.password}
              onChange={(e) => setDraft({ ...draft, password: e.target.value })}
            />

            <FormControl size="small">
              <InputLabel>{t("role") || "Role"}</InputLabel>
              <Select
                value={draft.role}
                label={t("role") || "Role"}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
              >
                {roleOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.value) || opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>{t("company") || "Company"}</InputLabel>
              <Select
                multiple
                value={draft.company_ids}
                label={t("company") || "Company"}
                onChange={(e) => setDraft({ ...draft, company_ids: e.target.value })}
              >
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              onClick={() => setDraft({ ...draft, company_ids: companies.map((c) => c.id) })}
            >
              {t("selectAllCompanies") || "Pasirinkti visas įmones"}
            </MDButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setCreateModalOpen(false)} color="secondary" variant="outlined">
            {t("cancel") || "Cancel"}
          </MDButton>
          <MDButton onClick={createUser} color="info" variant="contained">
            {t("addUser") || "Add user"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog
        open={passwordModal.open}
        onClose={() => {
          setPasswordModal({ open: false, user: null });
          setPasswordDraft({ p1: "", p2: "" });
        }}
      >
        <DialogTitle>{t("setPassword") || "Set password"}</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} flexDirection="column" sx={{ mt: 1, minWidth: 420 }}>
            <label>{t("newPassword") || "New password"}</label>
            <input
              type="password"
              value={passwordDraft.p1}
              onChange={(e) => setPasswordDraft((s) => ({ ...s, p1: e.target.value }))}
            />

            <label>{t("confirmPasswordLabel") || "Confirm password"}</label>
            <input
              type="password"
              value={passwordDraft.p2}
              onChange={(e) => setPasswordDraft((s) => ({ ...s, p2: e.target.value }))}
            />

            {passwordDraft.p1 && passwordDraft.p2 && passwordDraft.p1 !== passwordDraft.p2 && (
              <span style={{ color: "#d32f2f", fontSize: 12 }}>
                {t("passwordsMustMatch") || "Passwords must match"}
              </span>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setPasswordModal({ open: false, user: null });
              setPasswordDraft({ p1: "", p2: "" });
            }}
            color="secondary"
            variant="outlined"
          >
            {t("cancel") || "Cancel"}
          </MDButton>
          <MDButton
            disabled={!passwordDraft.p1 || passwordDraft.p1 !== passwordDraft.p2}
            onClick={setNewPassword}
            color="warning"
            variant="contained"
          >
            {t("save") || "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Manage Companies Dialog */}
      <Dialog
        open={manageCompaniesModal.open}
        onClose={() => setManageCompaniesModal({ open: false, user: null })}
      >
        <DialogTitle>{t("companyManagement") || "Company management"}</DialogTitle>
        <DialogContent>
          {manageCompaniesModal.user && (
            <Box display="flex" flexDirection="column" gap={2} sx={{ minWidth: 480 }}>
              <Box display="flex" gap={1} flexWrap="wrap">
                {(manageCompaniesModal.user.companies || []).map((c) => (
                  <Chip
                    key={c.id}
                    label={c.name}
                    onDelete={() => removeUserFromCompany(manageCompaniesModal.user.id, c.id)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
              <FormControl fullWidth size="small">
                <InputLabel>{t("company") || "Company"}</InputLabel>
                <Select
                  value={selectedCompanyId}
                  label={t("company") || "Company"}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                >
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setManageCompaniesModal({ open: false, user: null })}
            color="secondary"
            variant="outlined"
          >
            {t("close") || "Close"}
          </MDButton>
          <MDButton
            onClick={async () => {
              if (!manageCompaniesModal.user || !selectedCompanyId) return;
              await api.post(`/api/admin/users/${manageCompaniesModal.user.id}/companies/add/`, {
                company_id: selectedCompanyId,
              });
              setSelectedCompanyId("");
              fetchUsers();
            }}
            color="info"
            variant="contained"
          >
            {t("assign") || "Assign"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
